<template>
  <v-container>
    <v-row>
      <v-col sm="10">
        <h4 class="white--text">Search History</h4>
      </v-col>

      <v-col sm="2">
        <div @click="toggleSearch">
          <v-icon
            class="cursorPointer"
            color="white"
            size="25"
            v-if="!showHistory"
            :data-test-id="`${dataTestId}__open-icon`"
            >add</v-icon
          >
          <v-icon
            class="cursorPointer"
            color="white"
            size="25"
            v-if="showHistory"
            :data-test-id="`${dataTestId}__close-icon`"
            >maximize</v-icon
          >
        </div>
      </v-col>
    </v-row>
    <transition name="slideUp">
      <div v-if="showHistory">
        <search-history-list
          v-if="showSearchList"
          @searchItemClicked="onSearchItemClick"
          :searchList="currentSearchHistoryList"
          :data-test-id="`${dataTestId}__list`"
        />
        <v-row
          v-if="showShowMoreButton"
          no-gutters
          align="center"
          justify="center"
        >
          <v-btn
            :ripple="false"
            color="#58e2c2"
            link
            class="pa-0 font12"
            @click="toggleShowMore"
            :data-test-id="`${dataTestId}__show-more-btn`"
          >
            <v-icon size="12" class="pl-0" v-if="!showMore">add</v-icon>
            <v-icon size="12" class="pl-0" v-if="showMore">remove</v-icon>
            <span v-if="!showMore">Show more</span>
            <span v-if="showMore">Show less</span>
          </v-btn>
        </v-row>
        <span v-if="!searchList.length">No recent search on this project</span>
      </div>
    </transition>
  </v-container>
</template>

<script>
import SearchCriteriaChipHelper from '@/helpers/SearchCriteriaChipHelper';
import SearchHistoryList from './SearchHistoryList.vue';
export default {
  components: { SearchHistoryList },
  data() {
    return {
      showHistory: true,
      searchList: [],
      allUsers: [],
      showMore: false,
      searchRange: [
        {
          id: 'updatedOn',
          label: 'synced at'
        },
        {
          id: 'createdOn',
          label: 'created at'
        }
      ],
      searchHistoryResponse: null
    };
  },
  props: {
    dataTestId:String
  },
  computed: {
    showSearchList() {
      return Boolean(this.searchList.length) && this.showHistory;
    },
    currentSearchHistoryList() {
      const currentCount = 5;
      return this.showMore
        ? this.searchList
        : this.searchList.slice(0, currentCount);
    },
    showShowMoreButton() {
      const minItems = 5;
      return this.searchList.length && this.searchList.length > minItems;
    }
  },
  methods: {
    toggleSearch() {
      this.showHistory = !this.showHistory;
    },

    // On click of an item
    onSearchItemClick(index) {
      const currentSearchItem = this.searchHistoryResponse[index];
      if (this.$route.fullPath !== currentSearchItem.queryString) {
        this.$router.push(currentSearchItem.queryString);
      }
    },
    /**
     * Function to get sourceType title
     * if input sourceType = 'o365_sharepoint'
     * expected output = 'Share Point'
     */
    getSourceType(sourceType) {
      return sourceType === 'All'
        ? 'All_DEFAULT'
        : this.connectorArchiveNameMap(sourceType).title;
    },
    /**
     * Mapping of userIds to get user details
     * @param {Array} userIds
     *
     * @return if getObj = true, then array of userDetails; else array of fullName
     */
    getNamesFromIds(userIds, getObj = false) {
      const result = (userIds || []).map(id => {
        const userDetail = (this.allUsers || []).find(user => user.id === id);
        const fullName = this.formatFullName(userDetail);
        const userObj = {
          id,
          name: fullName
        };
        return getObj ? userObj : fullName;
      });
      return result;
    },
    /**
     * To get users list
     */
    async getUsers() {
      try {
        let users = await this.$services.user.loadUsersAll();
        users = this.parseResponse(users);
        if (users !== false) {
          this.allUsers = users[0].results;
        } else {
          this.snackbar('error', this.$message.CANT_LOAD_USERS);
        }
      } catch (err) {
        this.snackbar('error', err);
      }
    },
    // Create search object
    // this search object is used by SearchCriteriaChipHelper
    getSearchObject(queryString, isVaultItem) {
      // 1st get only advance search query param by filter out other query params
      // like sortBy= , sortOrder= params
      const advanceSearchQueryString = isVaultItem
        ? queryString.split('&advanceSearchParams=')?.[1]
        : queryString.split('&sort')?.[0];

      const value = JSON.parse(decodeURIComponent(advanceSearchQueryString));
      if (Object.keys(value).length) {
        let objSearchParams;
        if (isVaultItem) {
          const basedOn = this.searchRange.find(
            item => item.id === (value?.searchRange || 'createdOn')
          );

          objSearchParams = {
            dates: {
              start: value?.['from'] || '',
              end: value?.['to'] || ''
            },
            contains: {
              MUST: this.getWords(value, 'MUST'),
              SHOULD: this.getWords(value, 'SHOULD'),
              MUST_NOT: this.getWords(value, 'MUST_NOT')
            },
            accessibleBy: this.getNamesFromIds(value?.accessibleBy),
            fileExtension: value?.fileExtension || [],
            fileNames: value?.fileNames || [],
            sourcePath: value?.sourcePath || '',
            isLatest:
              value?.latestVersion || (!value?.sourceType ? false : undefined),
            sourceType: this.getSourceType(value?.sourceType || 'All'),
            basedOn: basedOn?.label
          };
        } else {
          objSearchParams = {
            dates: {
              start: value['startDate'],
              end: value['endDate']
            },
            from: value['fromEmail'],
            to: value['toEmail'],
            subject: value['subject'],
            contains: {
              MUST: this.getWords(value, 'MUST'),
              SHOULD: this.getWords(value, 'SHOULD'),
              MUST_NOT: this.getWords(value, 'MUST_NOT')
            },
            emailCategory: value['searchScope'],
            hasAttachments: value['hasAttachments'],
            attachments: value['attachments']
          };
        }
        return objSearchParams;
      } else {
        return {};
      }
    },
    getWords(searchParams, containsType) {
      if (!searchParams['hasWords']) {
        return [];
      }
      let filtered = searchParams['hasWords'].filter(
        param => param.containsType === containsType
      );
      if (filtered.length) {
        return filtered[0]['hasWords'];
      }
      return [];
    },
    async getSearchHistory() {
      const projectId = this.$route.params?.projectId;
      try {
        const response = await this.$services.project.getSearchHistory({
          projectId
        });
        const parsedResponse = this.parseResponse(response);
        if (!parsedResponse) {
          throw new Error(this.$message.SEARCH_HISTORY.PARSING_ERROR);
        }
        this.searchHistoryResponse = parsedResponse;
        const chipHelper = new SearchCriteriaChipHelper();

        this.searchList = (parsedResponse || []).map(listItem => {
          try {
            return chipHelper.getSearchCriteriaChips(
              this.getSearchObject(
                listItem.queryString.split('qs=')?.[1],
                this.isVaultItem(listItem.queryString)
              )
            );
          } catch (err) {
            this.$rapid7.log(JSON.stringify(err));
          }
        });
      } catch (err) {
        console.log(err);
        this.snackbar('error', err);
      }
    },
    isVaultItem(fullPath) {
      return fullPath?.includes('vault');
    },
    toggleShowMore() {
      this.showMore = !this.showMore;
    }
  },
  async mounted() {
    await this.getUsers();
    this.getSearchHistory();
  }
};
</script>

<style>
.latest-list {
  list-style: none;
  padding: 0 !important;
  width: 100%;
  margin-left: 0;
}

.latest-list li {
  padding: 10px !important;
  background: white;
  margin-bottom: 10px;
  display: flex;
  vertical-align: middle;
}

.latest-list li .image {
  width: 30%;
  text-align: center;
}

.latest-list li img {
  width: 60px;
  height: 60px;
  border-radius: 50%;
}

.latest-list li .data {
  text-align: center;
  /* display: inline-block; */
  /* overflow: hidden; */
  margin-top: 14px;
}

.latest-list li .data p {
  /* margin: 5px !important; */
  margin-bottom: 8px;
}

.latest-list li .data .date {
  color: #58e2c2;
  margin-bottom: 4px;
  font-size: 10px;
  font-weight: 700;
  line-height: 10px;
  text-align: left;
}

.latest-list li .data .email {
  color: #1b2342;
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;
  text-align: left;
}

.latest-list li .data .text {
  color: #1b2342;
  word-break: break-word;
  font-size: 14px;
  font-weight: 400;
  line-height: 26px;
  text-align: left;
  overflow: hidden;
  margin-bottom: 0;
}
</style>
<style scoped>
.latest-list li .data .text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
